"use client";

import { motion } from "framer-motion";
import {
  Briefcase,
  Code2,
  FlaskConical,
  Gavel,
  HardHat,
  LineChart,
  Microscope,
  Stethoscope,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { type ComponentType, useEffect, useState } from "react";

import type { AudienceSlug } from "../[audience]/_config/audiences";
import { captureCtaClick, type CtaLocation } from "./cta-tracking";

const REDACT_CHARS = "█▓▒░";

function useRedactedBlocks(length: number): string {
  const [text, setText] = useState("");
  useEffect(() => {
    const tick = () => {
      let s = "";
      for (let i = 0; i < length; i++) {
        s += REDACT_CHARS[Math.floor(Math.random() * REDACT_CHARS.length)];
      }
      setText(s);
    };
    tick();
    const id = setInterval(tick, 110);
    return () => {
      clearInterval(id);
    };
  }, [length]);
  return text;
}

type IconType = ComponentType<{ size?: number; className?: string }>;

// The badge can only render these eight personas. Exported so AudienceConfig
// can type badgePersonaId as this union instead of a bare string - a typo then
// fails the build rather than silently falling back to PERSONAS[0] at runtime.
export type PersonaId =
  | "researcher"
  | "engineer"
  | "product"
  | "lawyer"
  | "construction"
  | "doctor"
  | "scientist"
  | "consultant";

interface Persona {
  id: PersonaId;
  photo: string | null;
  name: string;
  title: string;
  flag: string;
  flagAlt: string;
  Icon: IconType;
  yrs: number;
  studies: number;
  rate: number;
  memberId: string;
  tier: { label: string; color: string };
}

const PERSONAS: Persona[] = [
  {
    id: "researcher",
    photo: "/marketing/experts/ai-training-headshot.jpg",
    name: "Dr. S. Mitchell",
    title: "Clinical Researcher",
    flag: "🇺🇸",
    flagAlt: "US",
    Icon: FlaskConical,
    yrs: 15,
    studies: 47,
    rate: 220,
    memberId: "TR-A47C-9183",
    tier: {
      label: "Tier 1",
      color: "var(--color-marketing-accent-green-dark)",
    },
  },
  {
    id: "engineer",
    photo: "/marketing/experts/expert-9.png",
    name: "J. Miller",
    title: "Sr. Software Engineer",
    flag: "🇺🇸",
    flagAlt: "US",
    Icon: Code2,
    yrs: 7,
    studies: 134,
    rate: 145,
    memberId: "TR-B22F-4501",
    tier: { label: "Verified", color: "var(--color-marketing-blue)" },
  },
  {
    id: "product",
    photo: "/marketing/experts/market-research-headshot.jpg",
    name: "D. Park",
    title: "VP of Product",
    flag: "🇺🇸",
    flagAlt: "US",
    Icon: LineChart,
    yrs: 12,
    studies: 89,
    rate: 195,
    memberId: "TR-C81E-2247",
    tier: {
      label: "Tier 1",
      color: "var(--color-marketing-accent-green-dark)",
    },
  },
  {
    id: "lawyer",
    photo: "/marketing/experts/expert-3.png",
    name: "O. Brown",
    title: "Corporate Counsel",
    flag: "🇬🇧",
    flagAlt: "UK",
    Icon: Gavel,
    yrs: 11,
    studies: 62,
    rate: 240,
    memberId: "TR-D14B-7702",
    tier: { label: "Pro", color: "var(--color-marketing-accent-purple)" },
  },
  {
    id: "construction",
    photo: "/marketing/experts/expert-7.png",
    name: "H. Ramirez",
    title: "Construction PM",
    flag: "🇺🇸",
    flagAlt: "US",
    Icon: HardHat,
    yrs: 9,
    studies: 31,
    rate: 95,
    memberId: "TR-E55A-1196",
    tier: { label: "Verified", color: "var(--color-marketing-blue)" },
  },
  {
    id: "doctor",
    photo: "/marketing/experts/expert-5.png",
    name: "Dr. C. Wei",
    title: "Cardiologist",
    flag: "🇨🇦",
    flagAlt: "CA",
    Icon: Stethoscope,
    yrs: 18,
    studies: 73,
    rate: 285,
    memberId: "TR-F09D-8810",
    tier: {
      label: "Tier 1",
      color: "var(--color-marketing-accent-green-dark)",
    },
  },
  {
    id: "scientist",
    photo: "/marketing/experts/expert-2.png",
    name: "P. Patel",
    title: "Senior Data Scientist",
    flag: "🇮🇳",
    flagAlt: "IN",
    Icon: Microscope,
    yrs: 8,
    studies: 56,
    rate: 165,
    memberId: "TR-G73C-5544",
    tier: { label: "Verified", color: "var(--color-marketing-blue)" },
  },
  {
    id: "consultant",
    photo: "/marketing/experts/expert-4.png",
    name: "M. Bauer",
    title: "Strategy Consultant",
    flag: "🇩🇪",
    flagAlt: "DE",
    Icon: Briefcase,
    yrs: 13,
    studies: 41,
    rate: 210,
    memberId: "TR-H62B-9038",
    tier: { label: "Pro", color: "var(--color-marketing-accent-purple)" },
  },
];

const BARCODE_BARS = [
  { id: "a", w: 3 },
  { id: "b", w: 1 },
  { id: "c", w: 2 },
  { id: "d", w: 1 },
  { id: "e", w: 3 },
  { id: "f", w: 2 },
  { id: "g", w: 1 },
  { id: "h", w: 2 },
  { id: "i", w: 3 },
  { id: "j", w: 1 },
  { id: "k", w: 2 },
  { id: "l", w: 1 },
  { id: "m", w: 3 },
  { id: "n", w: 2 },
  { id: "o", w: 1 },
  { id: "p", w: 3 },
  { id: "q", w: 1 },
  { id: "r", w: 2 },
  { id: "s", w: 1 },
  { id: "t", w: 2 },
];

const CYCLE_MS = 5000;
const GLAZE_MS = 700;
const SCRAMBLE_MS = 850;
const COUNTUP_MS = 750;
const SCRAMBLE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ#$@*&%";

function useScrambledText(target: string, trigger: number): string {
  const [display, setDisplay] = useState(target);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / SCRAMBLE_MS);
      const out = target
        .split("")
        .map((c, i) => {
          if (c === " " || c === "." || c === "-") {
            return c;
          }
          const lockAt = (i / Math.max(target.length, 1)) * 0.65 + 0.3;
          if (t >= lockAt) {
            return c;
          }
          return SCRAMBLE_CHARS[
            Math.floor(Math.random() * SCRAMBLE_CHARS.length)
          ]!;
        })
        .join("");
      setDisplay(out);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger is the intentional re-run signal
  }, [trigger]);
  return display;
}

function useCountUp(target: number, trigger: number): number {
  const [value, setValue] = useState(target);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / COUNTUP_MS);
      const eased = 1 - (1 - t) ** 3;
      setValue(Math.floor(target * eased));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return value;
}

function useScrambledHex(target: string, trigger: number): string {
  const [display, setDisplay] = useState(target);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const hex = "0123456789ABCDEF";
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / SCRAMBLE_MS);
      const out = target
        .split("")
        .map((c, i) => {
          if (c === "-") {
            return "-";
          }
          const lockAt = (i / Math.max(target.length, 1)) * 0.7 + 0.25;
          if (t >= lockAt) {
            return c;
          }
          return hex[Math.floor(Math.random() * hex.length)]!;
        })
        .join("");
      setDisplay(out);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return display;
}

interface BadgeSessionUser {
  image?: string | null;
  name?: string | null;
  email?: string | null;
}

interface ExpertsHeroBadgeProps {
  href?: string;
  initialPersonaId?: string;
  /** Override the wrapper className; defaults to the original /experts overlap. */
  wrapperClassName?: string;
  // Audience landings pass both to track the click; the generic /experts page
  // leaves them undefined and the badge skips the capture.
  audience?: AudienceSlug;
  ctaLocation?: CtaLocation;
  // Minimal shape the badge reads from the signed-in session. Typed
  // structurally (not better-auth's Session) so client useSession() data
  // assigns without nominal type clashes.
  session?: { user: BadgeSessionUser } | null;
}

export default function ExpertsHeroBadge({
  href = "/login",
  initialPersonaId,
  wrapperClassName = "pointer-events-none relative z-10 -mt-[520px] select-none",
  audience,
  ctaLocation,
  session,
}: ExpertsHeroBadgeProps = {}) {
  const signedInUser = session?.user;
  const isSignedIn = Boolean(signedInUser);
  const router = useRouter();
  const posthog = usePostHog();
  const initialIdx = (() => {
    if (!initialPersonaId) {
      return 0;
    }
    const found = PERSONAS.findIndex((p) => p.id === initialPersonaId);
    return found === -1 ? 0 : found;
  })();
  const [idx, setIdx] = useState(initialIdx);
  const [glazing, setGlazing] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [clicking, setClicking] = useState(false);

  useEffect(() => {
    if (isSignedIn) {
      return;
    }
    const interval = setInterval(() => {
      setGlazing(true);
      const t1 = setTimeout(() => {
        setIdx((i) => (i + 1) % PERSONAS.length);
        const t2 = setTimeout(() => {
          setGlazing(false);
        }, 100);
        return () => {
          clearTimeout(t2);
        };
      }, GLAZE_MS - 100);
      return () => {
        clearTimeout(t1);
      };
    }, CYCLE_MS);
    return () => {
      clearInterval(interval);
    };
  }, [isSignedIn]);

  const persona = PERSONAS[idx]!;
  const Icon = persona.Icon;
  const displayName = useScrambledText(persona.name, idx);
  const displayTitle = useScrambledText(persona.title, idx);
  const displayId = useScrambledHex(persona.memberId, idx);
  const yrs = useCountUp(persona.yrs, idx);
  const studies = useCountUp(persona.studies, idx);
  const rate = useCountUp(persona.rate, idx);
  const redactedTitle = useRedactedBlocks(18);

  const handleClick = () => {
    if (clicking) {
      return;
    }
    if (audience && ctaLocation) {
      captureCtaClick(posthog, { audience, ctaLocation });
    }
    setClicking(true);
    setTimeout(() => {
      router.push(isSignedIn ? "/me" : href);
    }, 240);
  };

  return (
    <motion.div
      initial={{ opacity: 0, transform: "translateY(-40px)" }}
      animate={{ opacity: 1, transform: "translateY(0px)" }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={wrapperClassName}
    >
      <style>{`
        @keyframes hero-badge-sway {
          0%, 100% { transform: rotate(-1.4deg); }
          50% { transform: rotate(1.4deg); }
        }
        @keyframes hero-badge-shimmer {
          0% { transform: translateX(-130%) skewX(-22deg); }
          100% { transform: translateX(260%) skewX(-22deg); }
        }
        @keyframes hero-badge-tier-pulse {
          0%, 100% { opacity: 0.92; }
          50% { opacity: 1; }
        }
        @keyframes hero-badge-icon-in {
          0% { transform: scale(0.4) rotate(-25deg); opacity: 0; }
          60% { transform: scale(1.1) rotate(8deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes hero-badge-flag-in {
          0% { transform: translateY(-6px) scale(0.7); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes hero-badge-photo-in {
          0% { transform: scale(1.06); filter: blur(6px) saturate(0.4); opacity: 0; }
          100% { transform: scale(1); filter: blur(0) saturate(1); opacity: 1; }
        }
        @keyframes hero-badge-stat-flicker {
          0%, 100% { opacity: 1; }
          92% { opacity: 1; }
          93%, 97% { opacity: 0.4; }
        }
        .hero-badge-pivot {
          transform-origin: 50% -90px;
          animation: hero-badge-sway 5.4s cubic-bezier(0.45, 0, 0.55, 1) infinite;
        }
        .hero-badge-shimmer {
          animation: hero-badge-shimmer 6.5s ease-in-out infinite;
          animation-delay: 1.2s;
        }
        .hero-badge-tier-pulse {
          animation: hero-badge-tier-pulse 2.8s ease-in-out infinite;
        }
        .hero-badge-icon-in {
          animation: hero-badge-icon-in 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
        .hero-badge-flag-in {
          animation: hero-badge-flag-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .hero-badge-photo-in {
          animation: hero-badge-photo-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .hero-badge-stat-flicker {
          animation: hero-badge-stat-flicker 4s steps(1) infinite;
        }
        .hero-badge-glaze {
          filter: blur(2px) saturate(0.7) brightness(1.05);
          transition: filter 200ms ease-in;
        }
        .hero-badge-clear {
          filter: blur(0) saturate(1) brightness(1);
          transition: filter 350ms ease-out;
        }
        .hero-badge-glaze-sweep {
          background: linear-gradient(
            105deg,
            transparent 0%,
            rgba(53, 97, 255, 0.0) 30%,
            rgba(53, 97, 255, 0.22) 50%,
            rgba(255, 255, 255, 0.38) 55%,
            rgba(53, 97, 255, 0.22) 60%,
            rgba(53, 97, 255, 0.0) 80%,
            transparent 100%
          );
          mix-blend-mode: screen;
          transform: translateX(-110%);
          transition: transform 700ms cubic-bezier(0.45, 0, 0.55, 1), opacity 200ms;
        }
        .hero-badge-glaze-sweep.active {
          transform: translateX(110%);
        }
      `}</style>

      {/* Preload all persona photos so the swap on cycle is instant. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
      >
        {PERSONAS.map(
          (p) =>
            p.photo && (
              <div
                key={`preload-${p.id}`}
                className="relative"
                style={{ width: 88, height: 88 }}
              >
                <Image
                  src={p.photo}
                  alt=""
                  fill
                  sizes="88px"
                  priority
                  draggable={false}
                />
              </div>
            ),
        )}
      </div>

      <div className="hero-badge-pivot relative flex flex-col items-center">
        {/* Lanyard cord; two splined strands extending up off-screen */}
        <svg
          width="180"
          height="500"
          viewBox="0 0 180 500"
          fill="none"
          className="relative z-0 -mb-3 overflow-visible"
          aria-hidden="true"
          preserveAspectRatio="none"
        >
          <path
            d="M86 0 C 84 130, 30 320, 90 500"
            stroke="#0a0a0a"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M94 0 C 96 130, 150 320, 90 500"
            stroke="#0a0a0a"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M86 0 C 84 130, 30 320, 90 500"
            stroke="rgba(255,255,255,0.16)"
            strokeWidth="0.8"
            strokeLinecap="round"
            fill="none"
            transform="translate(-0.6, 0.6)"
          />
          <path
            d="M94 0 C 96 130, 150 320, 90 500"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="0.8"
            strokeLinecap="round"
            fill="none"
            transform="translate(0.6, 0.6)"
          />
        </svg>

        {/* Badge body */}
        <div className="relative">
          {/* Grommet */}
          <div className="absolute top-[-7px] left-1/2 z-30 h-[14px] w-[36px] -translate-x-1/2">
            <div className="border-marketing-border bg-marketing-border-light absolute inset-0 rounded-full border" />
            <div className="bg-marketing-cream-light absolute top-1/2 left-1/2 h-[6px] w-[16px] -translate-x-1/2 -translate-y-1/2 rounded-full" />
            <div className="bg-marketing-blue absolute top-1/2 left-2 h-1 w-1 -translate-y-1/2 rounded-full" />
            <div className="bg-marketing-blue absolute top-1/2 right-2 h-1 w-1 -translate-y-1/2 rounded-full" />
          </div>

          <motion.div
            animate={{
              scale: clicking ? 0.84 : hovering ? 1.02 : 1,
              opacity: clicking ? 0.85 : 1,
            }}
            transition={{
              duration: clicking ? 0.22 : 0.18,
              ease: clicking ? [0.32, 0, 0.67, 0] : [0.16, 1, 0.3, 1],
            }}
            onMouseEnter={() => {
              setHovering(true);
            }}
            onMouseLeave={() => {
              setHovering(false);
            }}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={isSignedIn ? "View your profile" : "Claim your profile"}
            className="border-marketing-border bg-marketing-cream pointer-events-auto relative w-[216px] cursor-pointer overflow-hidden rounded-[16px] border"
            style={{
              transformOrigin: "50% 0%",
              boxShadow:
                "inset 1.5px -1.5px 3px rgba(96, 96, 96, 0.22), 4px 9px 20px rgba(0, 6, 183, 0.28)",
            }}
          >
            {/* Header strip */}
            <div className="bg-marketing-blue flex items-center justify-between px-3 py-1.5">
              <span className="text-marketing-cream font-mono text-[9px] tracking-[0.22em] uppercase">
                Expert
              </span>
              <div
                key={`tier-${isSignedIn ? "self" : hovering ? "u" : idx}`}
                className="hero-badge-flag-in flex items-center gap-1"
              >
                <div
                  className="hero-badge-tier-pulse h-1.5 w-1.5 rounded-full"
                  style={{
                    background: isSignedIn
                      ? "var(--color-marketing-accent-green-dark)"
                      : hovering
                        ? "var(--color-marketing-accent-red)"
                        : persona.tier.color,
                  }}
                />
                <span className="text-marketing-cream/90 font-mono text-[8.5px] tracking-wider uppercase">
                  {isSignedIn
                    ? "Active"
                    : hovering
                      ? "Unclaimed"
                      : persona.tier.label}
                </span>
              </div>
            </div>

            <div className={glazing ? "hero-badge-glaze" : "hero-badge-clear"}>
              {/* Photo + profession icon */}
              <div className="flex flex-col items-center px-3 pt-3 pb-2">
                {isSignedIn ? (
                  <div className="border-marketing-border bg-marketing-cream-light relative h-[88px] w-[88px] rounded-full border-2">
                    <div className="absolute inset-0 overflow-hidden rounded-full">
                      {signedInUser?.image ? (
                        <Image
                          src={signedInUser.image}
                          alt=""
                          fill
                          sizes="88px"
                          className="object-cover"
                          draggable={false}
                        />
                      ) : (
                        <div className="bg-marketing-blue flex h-full w-full items-center justify-center text-[32px] font-semibold text-white">
                          {String(
                            signedInUser?.name ?? signedInUser?.email ?? "?",
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>
                ) : hovering ? (
                  <div className="border-marketing-border bg-marketing-cream-light relative flex h-[88px] w-[88px] items-center justify-center rounded-full border-2 border-dashed">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <circle
                        cx="24"
                        cy="17"
                        r="7.5"
                        fill="var(--color-marketing-border)"
                        opacity="0.55"
                      />
                      <path
                        d="M8 42 C8 31, 15 27, 24 27 C33 27, 40 31, 40 42 Z"
                        fill="var(--color-marketing-border)"
                        opacity="0.55"
                      />
                    </svg>
                    <div className="bg-marketing-blue border-marketing-cream absolute -right-1 -bottom-1 flex h-[28px] w-[28px] items-center justify-center rounded-full border-[3px]">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M6 1.5 V10.5 M1.5 6 H10.5"
                          stroke="white"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <div
                    key={`photo-${idx}`}
                    className="border-marketing-border bg-marketing-cream-light hero-badge-photo-in relative h-[88px] w-[88px] rounded-full border-2"
                  >
                    <div className="absolute inset-0 overflow-hidden rounded-full">
                      {persona.photo && (
                        <Image
                          src={persona.photo}
                          alt=""
                          fill
                          sizes="88px"
                          className="object-cover"
                          draggable={false}
                        />
                      )}
                    </div>
                    <div
                      key={`icon-${idx}`}
                      className="bg-marketing-blue border-marketing-cream hero-badge-icon-in absolute -right-1 -bottom-1 flex h-[28px] w-[28px] items-center justify-center rounded-full border-[3px]"
                    >
                      <Icon size={14} className="text-marketing-cream" />
                    </div>
                  </div>
                )}
              </div>

              {/* Name + flag */}
              <div className="flex flex-col items-center gap-0.5 px-3 pb-1">
                <div className="flex h-[14px] items-center gap-1.5">
                  {isSignedIn ? (
                    <span className="text-foreground font-mono text-[11px] leading-none font-semibold tracking-[-0.02em]">
                      {signedInUser?.name ?? "Expert"}
                    </span>
                  ) : hovering ? (
                    <span className="text-foreground text-[14px] leading-none font-bold tracking-[-0.025em]">
                      You?
                    </span>
                  ) : (
                    <>
                      <span className="text-foreground font-mono text-[11px] leading-none font-semibold tracking-[-0.02em]">
                        {displayName}
                      </span>
                      <span
                        key={`flag-${idx}`}
                        className="hero-badge-flag-in text-[12px] leading-none"
                        aria-label={persona.flagAlt}
                      >
                        {persona.flag}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-marketing-text-muted/60 font-mono text-[8.5px] tracking-[0.05em] uppercase">
                  {isSignedIn
                    ? (signedInUser?.email ?? "")
                    : hovering
                      ? redactedTitle
                      : displayTitle}
                </span>
              </div>

              {/* Stats grid: YRS / STUDIES / RATE */}
              <div className="border-marketing-border-light mx-3 mt-1.5 mb-2 grid grid-cols-3 gap-1 rounded-[6px] border bg-white/40 px-1 py-1.5">
                <Stat
                  label="YRS"
                  value={isSignedIn ? "-" : hovering ? "?" : String(yrs)}
                />
                <Stat
                  label="STUDIES"
                  value={isSignedIn ? "-" : hovering ? "?" : String(studies)}
                />
                <Stat
                  label="RATE"
                  value={isSignedIn ? "-" : hovering ? "$?" : `$${rate}`}
                />
              </div>

              {/* Attestation row */}
              <div className="flex items-center justify-center gap-1 px-3 pb-2">
                {["ID", "LI", "EM", "IP"].map((label) => (
                  <div
                    key={label}
                    className="border-marketing-border bg-marketing-cream-light flex h-[16px] items-center justify-center gap-0.5 rounded-full border px-1.5"
                  >
                    <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
                      <path
                        d="M0.8 3 L2.4 4.6 L5.2 1.4"
                        stroke="var(--color-marketing-accent-green-dark)"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                    <span className="text-marketing-text-secondary font-mono text-[7px] tracking-wider">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Barcode strip */}
              <div className="border-marketing-border-light flex items-end gap-[1.5px] border-t px-3 py-1.5">
                {BARCODE_BARS.map((bar) => (
                  <div
                    key={bar.id}
                    className="bg-marketing-text-muted/80"
                    style={{ width: `${bar.w}px`, height: "12px" }}
                  />
                ))}
              </div>

              {/* Footer with Terac wordmark + member ID */}
              <div className="border-marketing-border-light bg-marketing-cream-light flex items-center justify-between border-t px-3 py-1.5">
                <div className="flex items-center gap-1">
                  <Image
                    src="/brand/logo.svg"
                    alt=""
                    width={10}
                    height={10}
                    draggable={false}
                  />
                  <span className="text-marketing-text-muted font-mono text-[8px] tracking-[0.18em] uppercase">
                    Terac
                  </span>
                </div>
                <span
                  className={`text-marketing-text-secondary font-mono text-[7.5px] tracking-[0.12em] ${isSignedIn ? "" : "hero-badge-stat-flicker"}`}
                >
                  {isSignedIn
                    ? "Member"
                    : hovering
                      ? "TR-????-????"
                      : displayId}
                </span>
              </div>
            </div>

            {/* Shimmer overlay */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div
                className="hero-badge-shimmer absolute inset-y-0 w-[40%]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%)",
                }}
              />
            </div>

            {/* Glaze-over sweep on transition */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div
                className={`hero-badge-glaze-sweep absolute inset-0 ${
                  glazing ? "active" : ""
                }`}
                style={{ opacity: glazing ? 1 : 0 }}
              />
            </div>

            {/* Hover-state "UNCLAIMED" stamp */}
            {hovering && !clicking && !isSignedIn && (
              <div
                className="text-marketing-accent-red-dark pointer-events-none absolute top-[42px] -right-2 border-2 px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-[0.22em] uppercase"
                style={{
                  borderRadius: "2px",
                  borderColor: "var(--color-marketing-accent-red-dark)",
                  background: "rgba(255, 255, 240, 0.5)",
                  transform: "rotate(-14deg)",
                }}
              >
                Unclaimed
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-foreground font-mono text-[11px] leading-none font-semibold tabular-nums">
        {value}
      </span>
      <span className="text-marketing-text-secondary mt-0.5 font-mono text-[6.5px] tracking-[0.15em] uppercase">
        {label}
      </span>
    </div>
  );
}
